/* global console */

import React, { useEffect, useCallback } from "react";
import { IComboBoxOption } from "@fluentui/react";
import { SourceTypeEnum } from "../../../../api/models/source-type-enum";
import { fetchSheet, Sheet } from "../../../../api/object/Form";
import { fetchSheetExcelFile } from "../../../../api/object/ExcelFile";
import { retrieveDocumentProp } from "../../../../utils/helpers/CustomXmlHelper";
import { useAppSelector } from "../../../../redux/hooks/hooks";
import { selectCpmUsername } from "../../../../redux/feature/settings/configurations-slice";
import { useFilteredElaborations } from "../../../generic/GenericElaborationSelector";
import {
  ElaborationStatusItem,
  elaborationStatusItemIsAdded,
} from "../../../../redux/feature/object/remote-elaborations-slice";

interface DropDownSheetState {
  sheetsItems: IComboBoxOption[];
  correlationId?: string;
  isOldValueForSheetToHide: boolean;
}

const useDropDownSheet = (props) => {
  const [state, setState] = React.useState<DropDownSheetState>({
    sheetsItems: [],
    isOldValueForSheetToHide: false,
  });
  const { sheetsItems, isOldValueForSheetToHide } = state;
  const {
    isEdit,
    type,
    sheetsAreaChartMap,
    isFileExcelExisting,
    isComponentInstance,
    isLocked,
    originalSheetArea,
    setOriginalSheetArea,
    setIsOldValueForAreaToHide,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    setIsLoadingSheetAndArea,
  } = props;
  const username = useAppSelector(selectCpmUsername);
  function selectSheetComboBox(option: IComboBoxOption) {
    props.selectSheetComboBox(option);
    if (props.objectSource === "EXCEL") {
      setOriginalSheetArea({ uid: option.key, desc: option.text, code: option.key });
      setState((s) => ({ ...s, isOldValueForSheetToHide: true }));
      props.setHasSheetToBeSelected(false);
      setIsOldValueForAreaToHide(true);
      props.setIsSheetChartAndAreaToHide(false);
    }
  }

  const sheetAndAreaElabFindFunction = useCallback(
    (item: ElaborationStatusItem) => {
      if (item && item.elabResult === "Error : Send failed") {
        props.setIsLoadingDataExcel(false);
        props.setIsLoadingSheetAndArea(false);
      }
      return (
        elaborationStatusItemIsAdded(item) &&
        item.elaborationType === "SHEET_AND_AREA" &&
        item.correlationId === state.correlationId &&
        !addedElaborationItemsList.find(
          (detail) => detail.correlationId === item.correlationId && detail.itemId === item.id
        )
      );
    },
    [state]
  );

  const filteredElaborationItem = useFilteredElaborations({
    findFunction: sheetAndAreaElabFindFunction,
  });
  const addedElaborationItemsList = useAppSelector((state) => state.remoteElaborations.addedElaborationItemsList);

  useEffect(() => {
    if (filteredElaborationItem && props.objectSource === SourceTypeEnum.EXCEL) {
      const sheets: Sheet[] = JSON.parse(filteredElaborationItem.elabResult);
      let foundSheet = sheets.find((value) => value.desc === props.sheet?.desc);
      if (!foundSheet && props.objectSourceImage?.includes("IMAGE")) {
        props.setIsSheetChartAndAreaToHide(true);
      } else {
        props.setIsSheetChartAndAreaToHide(false);
      }
      const items: IComboBoxOption[] = sheets.map((sheet) => {
        if (originalSheetArea?.code === originalSheetArea?.desc && sheet.code === originalSheetArea?.code) {
          originalSheetArea.code = sheet.uid;
          setOriginalSheetArea(originalSheetArea);
        }
        sheetsAreaChartMap.set(sheet.uid?.trim(), props.type === "CHART" ? sheet.charts : sheet.areas);
        return {
          key: sheet.uid?.trim(),
          text: sheet.desc,
        };
      });
      let foundOldSheet = false;
      if (isEdit && originalSheetArea?.code === originalSheetArea?.desc) {
        let oldSheet = sheets.find((value) => value.code === originalSheetArea.code);
        foundOldSheet = !(oldSheet && oldSheet.code !== "");
        props.setIsOldSheetOrAreaSelected(true);
        props.setHasSheetToBeSelected(true);
      } else {
        props.setIsOldSheetOrAreaSelected(false);
        props.setHasSheetToBeSelected(false);
      }

      setState({
        ...state,
        sheetsItems: items,
        isOldValueForSheetToHide: !foundOldSheet,
      });
      setIsOldValueForAreaToHide(!foundOldSheet);
      props.setIsLoadingDataExcel(false);
      props.setIsLoadingSheetAndArea(false);
      props.initAreas(originalSheetArea);
    }
  }, [filteredElaborationItem?.elabResult]);

  useEffect(() => {
    if (props.form != null && props.dbApplication != null && props.objectSource !== SourceTypeEnum.EXCEL) {
      fetchSheet(props.dbApplication.id, props.form.code, type).then((sheets) => {
        const items: IComboBoxOption[] = sheets.map((sheet) => {
          sheetsAreaChartMap.set(sheet.code, type == "CHART" ? sheet.charts : sheet.areas);
          return {
            key: sheet.code,
            text: sheet.desc,
          };
        });
        setState({ ...state, sheetsItems: items });
        props.setIsLoadingDataExcel(false);
        props.setIsLoadingSheetAndArea(false);
        props.initAreas(props.sheet);
      });
    } else if (props.form != null && props.objectSource === SourceTypeEnum.EXCEL) {
      if (isFileExcelExisting) {
        setState({ ...state, sheetsItems: [] });
        props.setIsLoadingDataExcel(true);
        props.setIsLoadingSheetAndArea(true);
        // eslint-disable-next-line no-undef
        console.log("DropDownSheetHook ....");
        retrieveDocumentProp("Document-ID").then((documentId) => {
          fetchSheetExcelFile(username, documentId, props.form.code, props.type).then((response) =>
            setState((s) => ({ ...s, correlationId: response.correlationId }))
          );
        });
      }
    }
  }, [props.form]);

  return {
    isEdit,
    sheetsItems,
    selectSheetComboBox,
    isFileExcelExisting,
    isComponentInstance,
    isLocked,
    originalSheetArea,
    setOriginalSheetArea,
    isOldValueForSheetToHide,
    setIsOldValueForAreaToHide,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    setIsLoadingSheetAndArea,
  };
};

export default useDropDownSheet;
